import { useAuth0 } from '@auth0/auth0-react';
import { openInNewTab } from '@strata/helper-functions';
import { Header as StrataHeader } from '@strata/react-header';
import { BaseNotification } from '@strata/react-notifications';
import { type ComponentProps, useEffect, useState } from 'react';

import { UserNotificationSettingsDialog } from 'components/header/dialogs/notificationSettings/UserNotificationSettingsDialog';
import { UserProfileDialog } from 'components/header/dialogs/UserProfileDialog';
import { FeatureCarousel } from 'components/header/FeatureCarousel';
import { NotificationComponentMap } from 'components/notifications/NotificationComponentMap';
import { clearDataForLogout } from 'services/authHelpers';
import { AppRoutes } from 'services/routes';
import { useAppDispatch, useAppSelector } from 'store';
import { useGetHomeScreenMessagesQuery } from 'store/api/announcementApi';
import { useOpenRequestCounts } from 'store/api/payerRequestApiThunks';
import {
    clearIncomingNotification,
    updateNotificationStatus,
    updateNotificationStatuses,
} from 'store/notification/notificationThunks';

export const Header = () => {
    const { isAuthenticated, logout } = useAuth0();
    const dispatch = useAppDispatch();

    const { data: requestCounts } = useOpenRequestCounts(!isAuthenticated);

    const navTabs = isAuthenticated
        ? [
              {
                  label: 'Requests',
                  to: AppRoutes.requests,
                  // Sum the counts for all request types across all payers
                  badgeCount: Object.values(requestCounts ?? {})
                      .map((payerRequestCounts) => Object.values(payerRequestCounts ?? {}))
                      .flat()
                      .reduce((sum, num) => sum + num, 0),
              },
          ]
        : [];

    const [showUserNotificationSettingsDialog, setShowUserNotificationSettingsDialog] =
        useState(false);
    const [showUserProfileDialog, setShowUserProfileDialog] = useState(false);

    // Load home screen messages & carousels on login
    useGetHomeScreenMessagesQuery(undefined, { skip: !isAuthenticated });

    const { notifications, incomingNotification } = useAppSelector((state) => state.notification);

    // Prevent incoming notifications from re-appearing
    useEffect(() => {
        dispatch(clearIncomingNotification());
    }, [incomingNotification, dispatch]);

    const onNotificationsDropdownClose = () => {
        const newlySeenIds = notifications
            .filter((notification) => notification.status === BaseNotification.Statuses.NEW)
            .map((notification) => notification.id);
        if (newlySeenIds.length > 0) {
            dispatch(updateNotificationStatuses(BaseNotification.Statuses.SEEN, newlySeenIds));
        }
    };
    const onNotificationClick: ComponentProps<typeof StrataHeader>['onNotificationClick'] = (
        id,
    ) => {
        dispatch(updateNotificationStatus(BaseNotification.Statuses.INTERACTED, id));
    };
    const onRemoveNotificationClick: ComponentProps<
        typeof StrataHeader
    >['onRemoveNotificationClick'] = (id) => {
        dispatch(updateNotificationStatus(BaseNotification.Statuses.DELETED, id));
    };
    const onClearAllNotificationsClick = () => {
        const idList = notifications.map((notification) => notification.id);
        dispatch(updateNotificationStatuses(BaseNotification.Statuses.DELETED, idList));
    };

    const onLogOutClick = () => {
        (async () => {
            clearDataForLogout();
            await logout({
                logoutParams: { returnTo: `${window.location.origin}${AppRoutes.loggedOut}` },
            });
        })();
    };

    return (
        <>
            <StrataHeader
                navBar={navTabs}
                onProfileClick={() => setShowUserProfileDialog(true)}
                onNotificationSettingsClick={() => setShowUserNotificationSettingsDialog(true)}
                onLogOutClick={onLogOutClick}
                showNotifications={isAuthenticated}
                notifications={notifications}
                incomingNotification={incomingNotification}
                NotificationComponentMap={NotificationComponentMap}
                onNotificationClick={onNotificationClick}
                onRemoveNotificationClick={onRemoveNotificationClick}
                onClearAllNotificationsClick={onClearAllNotificationsClick}
                onNotificationsDropdownClose={onNotificationsDropdownClose}
                showProfileDropdown={isAuthenticated}
                onTermsOfUseClick={() => openInNewTab(AppRoutes.termsOfUse)}
                onPrivacyPolicyClick={() => openInNewTab(AppRoutes.privacyPolicy)}
                appVersion={process.env.REACT_APP_VERSION}
                fmhEnvironmentForTag={process.env.REACT_APP_ENV}
            />
            {isAuthenticated && (
                <>
                    <UserNotificationSettingsDialog
                        onSecondaryButtonClick={() => setShowUserNotificationSettingsDialog(false)}
                        isOpen={showUserNotificationSettingsDialog}
                    />
                    <UserProfileDialog
                        onSecondaryButtonClick={() => setShowUserProfileDialog(false)}
                        isOpen={showUserProfileDialog}
                    />
                    <FeatureCarousel />
                </>
            )}
        </>
    );
};
